import { StyleSheet, Platform, type ViewStyle } from 'react-native';
import { layoutLtrStyle } from '../../shared/i18n';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { SUBSCRIPTION_PLAN_CONTENT_WIDTH } from '../../dashboard/components/variants/set2/SubscriptionPlanEdgeImage';
import { SUBSCRIPTION_SCREEN_PADDING_H } from '../subscriptionScreenLayout';

const CARD_RADIUS = (appTheme.borderRadius && appTheme.borderRadius.lg) ?? 20;
const CHIP_GAP = (appTheme.spacing && appTheme.spacing.sm) ?? 8;

export function createSubscriptionPremiumListStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: c.background,
    },
    stateBelowHeader: {
      flex: 1,
      paddingHorizontal: SUBSCRIPTION_SCREEN_PADDING_H,
    },
    headerRefreshWrap: { minWidth: 44, minHeight: 38 },
    headerRefreshInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 44,
      minHeight: 38,
      borderRadius: 10,
    },
    horizontalLtr: { ...layoutLtrStyle },
    clickableFill: {
      flex: 1,
      alignSelf: 'stretch',
    },
    clickableFillWidth: {
      width: '100%',
      alignSelf: 'stretch',
    },
    scroll: { flex: 1 },
    scrollContent: {
      paddingHorizontal: SUBSCRIPTION_SCREEN_PADDING_H,
    },
    categoryStrip: {
      marginHorizontal: -SUBSCRIPTION_SCREEN_PADDING_H,
      marginBottom: appTheme.spacing.lg,
    },
    categoryStripLtr: {
      ...layoutLtrStyle,
    },
    categoryStripContent: {
      alignItems: 'center',
    },
    categoryCardMediaMask: {
      flex: 1,
      width: '100%',
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      position: 'relative',
    },
    categoryCard: {
      borderRadius: CARD_RADIUS / 2,
      overflow: 'hidden',
      backgroundColor: c.surface,
    },
    categoryCardClip: {
      flex: 1,
      overflow: 'hidden',
    },
    categoryCardImageArea: {
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },
    categoryCardTitleBar: {
      paddingHorizontal: appTheme.spacing?.md ?? 12,
      paddingVertical: appTheme.spacing?.sm ?? 8,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
      backgroundColor: c.surface,
      justifyContent: 'center',
      minHeight: 44,
    },
    categoryCardActive: {
      borderRadius: CARD_RADIUS,
    },
    categoryCardImage: {
      width: '100%',
      height: '100%',
    },
    categoryCardFallback: {
      flex: 1,
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    categoryCardTitle: {
      ...(appTheme.typography?.bodyMedium ?? {}),
      fontWeight: '700',
      fontSize: 13,
      lineHeight: 17,
      color: c.text,
    },
    sectionLabel: {
      ...(appTheme.typography?.bodyMedium ?? {}),
      color: c.text,
      marginBottom: appTheme.spacing?.sm ?? 8,
      fontWeight: '600',
    },
    chipRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: CHIP_GAP,
      marginBottom: appTheme.spacing?.lg ?? 16,
      ...layoutLtrStyle,
    },
    chip: {
      minWidth: 56,
      height: 44,
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: c.borderLight || c.primaryFade,
      backgroundColor: c.surface,
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.06,
          shadowRadius: 4,
        },
        android: { elevation: 2 },
        default: {},
      }),
      justifyContent: 'center',
      alignItems: 'center',
    },
    chipActive: {
      borderColor: 'transparent',
      ...Platform.select({
        ios: { shadowOpacity: 0.12, shadowRadius: 8 },
        android: { elevation: 4 },
        default: {},
      }),
    },
    chipGradientFill: {
      ...StyleSheet.absoluteFillObject,
      borderRadius: 12,
    },
    chipContentFill: {
      flex: 1,
      alignSelf: 'stretch',
      width: '100%',
      minHeight: 44,
      justifyContent: 'center',
      alignItems: 'center',
    },
    chipMember: {
      flex: 1,
      minWidth: 0,
      maxWidth: 120,
    },
    chipDuration: {
      flex: 1,
      minWidth: 56,
      minHeight: 44,
    },
    chipText: {
      ...(appTheme.typography?.bodyMedium ?? {}),
      fontSize: 17,
      fontWeight: '600',
      color: c.text,
      textAlign: 'center',
      zIndex: 2,
    },
    chipTextActive: {
      color: c.onPrimary,
      fontWeight: '700',
    },
    planListContainer: {
      width: '100%',
    },
    planListGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: appTheme.spacing.sm,
      justifyContent: 'space-between',
    },
    planCardOuter: {
      width: '100%',
    },
    planCardOuterGrid: {
      width: '48%',
      minWidth: 0,
    },
    planCardRoot: {
      flexDirection: 'row',
      alignItems: 'stretch',
      position: 'relative',
      minHeight: 120,
      marginBottom: appTheme.spacing.md,
      borderRadius: appTheme.card.radius,
      overflow: 'hidden',
      backgroundColor: c.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.border,
      ...layoutLtrStyle,
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.1,
          shadowRadius: 12,
        },
        android: { elevation: 4 },
      }),
    },
    planCardRootSelected: {
      borderWidth: 2,
      borderColor: c.primary,
    },
    planCardContent: {
      flex: 1,
      minWidth: 0,
      maxWidth: SUBSCRIPTION_PLAN_CONTENT_WIDTH,
      width: SUBSCRIPTION_PLAN_CONTENT_WIDTH,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: 10,
      paddingStart: appTheme.spacing.sm,
      paddingEnd: appTheme.spacing.md,
      justifyContent: 'center',
      flexDirection: 'column',
    },
    planCardCheckBadge: {
      position: 'absolute',
      zIndex: 3,
      top: 10,
      width: 30,
      height: 30,
      borderRadius: 15,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.2,
          shadowRadius: 2,
        },
        android: { elevation: 3 },
      }),
    },
    planCardTitle: {
      ...appTheme.typography.body,
      fontWeight: '800',
      fontSize: 15,
      lineHeight: 20,
      color: c.text,
      marginBottom: 6,
    },
    planCardMembersLine: {
      ...appTheme.typography.caption,
      fontSize: 12,
      color: c.textSecondary,
      marginBottom: 4,
    },
    planCardMealsLine: {
      ...appTheme.typography.caption,
      fontSize: 12,
      lineHeight: 16,
      color: c.textSecondary,
      marginBottom: 4,
    },
    planCardPriceRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 8,
      maxWidth: '100%',
      justifyContent: 'flex-end',
      alignSelf: 'flex-end',
      width: '100%',
    },
    planCardListPrice: {
      ...(appTheme.typography?.caption ?? { fontSize: 13 }),
      textDecorationLine: 'line-through',
      fontSize: 13,
      flexShrink: 0,
    },
    planCardPricePillShell: {
      borderRadius: (appTheme.borderRadius && appTheme.borderRadius.md) ?? 20,
      overflow: 'hidden',
      alignSelf: 'flex-start',
      flexShrink: 0,
      position: 'relative',
    },
    planCardPricePillInset: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: 7,
      zIndex: 1,
    },
    planCardPriceText: {
      ...(appTheme.typography?.bodySmall ?? {}),
      fontSize: 13,
      fontWeight: '600',
      letterSpacing: 1,
      color: c.onPrimary,
    },
    bottomBarShell: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 20,
      borderTopLeftRadius: CARD_RADIUS + 8,
      borderTopRightRadius: CARD_RADIUS + 8,
      overflow: 'hidden',
      backgroundColor: c.primary,
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: -3 },
          shadowOpacity: 0.2,
          shadowRadius: 12,
        },
        android: { elevation: 10 },
        default: {},
      }),
    },
    bottomBarHairline: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: StyleSheet.hairlineWidth,
      backgroundColor: hexToRgba(c.onPrimary, 0.22),
      zIndex: 2,
    },
    bottomBarPressable: {
      width: '100%',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: SUBSCRIPTION_SCREEN_PADDING_H,
      minHeight: 72,
      justifyContent: 'center',
    },
    bottomBarPressableDisabled: {
      opacity: 0.52,
    },
    bottomBarPressablePressed: {
      opacity: 0.92,
    },
    bottomBarRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.md,
    },
    bottomBarRowRtl: {
      flexDirection: 'row-reverse',
    },
    bottomBarTextBlock: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'center',
    },
    bottomBarPlanLine: {
      ...(appTheme.typography?.body ?? {}),
      fontSize: 16,
      lineHeight: 22,
      fontWeight: '600',
      color: c.onPrimary ?? c.surface,
      marginBottom: 5,
    },
    bottomBarTotalLine: {
      ...(appTheme.typography?.bodySmall ?? {}),
      fontSize: 13,
      lineHeight: 18,
      fontWeight: '500',
      color: hexToRgba(c.onPrimary, 0.88),
      letterSpacing: 0.2,
    },
    bottomBarCtaBadge: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: hexToRgba(c.onPrimary, 0.2),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.onPrimary, 0.38),
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
    },
    freeDaysSheetBody: {
      paddingHorizontal: appTheme.spacing.lg,
      gap: appTheme.spacing.md,
    },
    freeDaysMessage: {
      ...appTheme.typography.body,
      color: c.text,
    },
    freeDaysHighlight: {
      fontWeight: '700',
      color: c.primary,
    },
    freeDaysOption: {
      borderWidth: 1,
      borderRadius: 12,
      padding: appTheme.spacing.md,
    },
    freeDaysOptionActive: {
      backgroundColor: hexToRgba(c.primary, 0.08),
      borderColor: c.primary,
    },
    freeDaysOptionText: {
      color: c.text,
      fontWeight: '500',
    },
    freeDaysOptionTextActive: {
      fontWeight: '700',
    },
    freeDaysConfirmBtn: {
      marginTop: appTheme.spacing.sm,
      backgroundColor: c.primary,
      borderRadius: 12,
      paddingVertical: 14,
      alignItems: 'center',
    },
    freeDaysConfirmText: {
      color: c.onPrimary,
      fontWeight: '700',
    },
    guestSampleBtn: {
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 12,
      backgroundColor: c.primary,
      alignSelf: 'center',
    },
    guestSampleBtnText: {
      color: c.onPrimary,
      fontWeight: '600',
      textAlign: 'center',
    },
  });
}

export type SubscriptionPremiumListStyles = ReturnType<typeof createSubscriptionPremiumListStyles>;

export const PREMIUM_CARD_RADIUS = CARD_RADIUS;

export function premiumCategoryInnerRadius(is_active: boolean): ViewStyle['borderRadius'] {
  return is_active ? CARD_RADIUS : CARD_RADIUS / 2;
}
